import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

const inputValueTypography = (theme: Theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  textAlign: "right" as const,
  verticalAlign: "middle" as const,
  leadingTrim: "cap-height",
});

export const useStyles = makeStyles((theme: Theme) => ({
  card: {
    width: "100%",
    boxSizing: "border-box",
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: theme.palette.background.paper,
    direction: "rtl",
  },
  section: { display: "flex", flexDirection: "column", gap: "12px", width: "100%" },

  sectionHeader: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: "8px",
    paddingBottom: "8px",
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
  sectionHeaderPlain: {
    paddingBottom: "8px",
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
  sectionTitle: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "21px",
    color: theme.palette.secondary.main,
    textAlign: "right",
    direction: "rtl",
  },
  grid5v: {
    display: "grid",
    gridTemplateColumns: "1.1fr 0.55fr 2fr 1fr 1.2fr",
    gap: "12px",
    width: "100%",
    direction: "rtl",
  },
  grid5: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: "12px",
    width: "100%",
    direction: "rtl",
  },
  grid7: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: "12px",
    flex: 1,
    minWidth: 0,
    alignItems: "end",
    direction: "rtl",
  },
  row: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-end",
    width: "100%",
    minWidth: 0,
  },
  chassisActions: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    flexShrink: 0,
    direction: "rtl",
    alignItems: "flex-start",
  },
  chassisButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    height: 48,
  },
  additions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    width: "100%",
    alignItems: "flex-end",
    direction: "rtl",
  },
  additionsField: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: 0,
    width: "100%",
    direction: "rtl",
    alignItems: "flex-start",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: 0,
    width: "100%",
    direction: "rtl",
    alignItems: "stretch",
  },
  fieldDense: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: "100%",
    direction: "rtl",
    alignItems: "stretch",
  },
  label: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "80%",
    letterSpacing: 0,
    margin: 0,
    padding: 0,
    color: theme.palette.text.primary,
    textAlign: "right",
    direction: "rtl",
    display: "block",
    width: "100%",
    boxSizing: "border-box",
  },
  labelSpacer: {
    height: "9.6px",
    margin: 0,
    padding: 0,
    visibility: "hidden",
  },
  selectControl: {
    margin: 0,
    width: "100%",
  },
  fieldValue: {
    ...inputValueTypography(theme),
    color: theme.palette.secondary.main,
  },
  input: {
    direction: "rtl",
    margin: 0,
    "& .MuiOutlinedInput-root": {
      margin: 0,
      height: 48,
      borderRadius: "4px",
      backgroundColor: theme.palette.background.paper,
      ...inputValueTypography(theme),
      color: theme.palette.secondary.main,
      boxSizing: "border-box",
      alignItems: "center",
      "& fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px 12px 8px",
      direction: "rtl",
      width: "100%",
      height: "auto",
      boxSizing: "border-box",
      ...inputValueTypography(theme),
      color: theme.palette.secondary.main,
      "&::placeholder": {
        color: theme.palette.text.disabled,
        opacity: 1,
      },
    },
    "& input[type=number]": {
      direction: "ltr",
      textAlign: "right",
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        WebkitAppearance: "none",
        margin: 0,
      },
  },
  inputReadOnly: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.action.hover,
    },
    "& .MuiOutlinedInput-input": {
      cursor: "default",
    },
  },
  inputWithSpinners: {
    "& input[type=number]": {
      direction: "rtl",
      textAlign: "right",
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        WebkitAppearance: "auto",
        margin: 0,
        opacity: 1,
      },
  },
  fieldCompact: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: 0,
    width: 219.25,
    flexShrink: 0,
    direction: "rtl",
    alignItems: "stretch",
  },
  inputCompact: {
    "& .MuiOutlinedInput-root": {
      width: 219.25,
      height: 48,
    },
  },
  fieldNarrow: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    flexShrink: 0,
    direction: "rtl",
    alignItems: "stretch",
  },
  inputNarrow: {
    width: 100,
    "& .MuiOutlinedInput-root": {
      width: 100,
      height: 48,
      borderRadius: "4px",
      boxSizing: "border-box",
      "& fieldset": {
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px 12px 8px",
    },
  },
  select: {
    direction: "rtl",
    height: 48,
    borderRadius: "4px",
    backgroundColor: theme.palette.background.paper,
    ...inputValueTypography(theme),
    color: theme.palette.secondary.main,
    boxSizing: "border-box",
    "& .MuiOutlinedInput-root": {
      height: 48,
      alignItems: "center",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
      borderWidth: "1px",
    },
    "& .MuiSelect-select": {
      padding: "12px 16px 12px 32px !important",
      direction: "rtl",
      boxSizing: "border-box",
      ...inputValueTypography(theme),
      color: theme.palette.secondary.main,
    },
    "& .MuiSelect-icon": {
      left: "12px !important",
      right: "auto !important",
    },
    "& .MuiSelect-iconOpen": {
      left: "12px !important",
      right: "auto !important",
    },
  },
  selectValue: {
    display: "block",
    width: "100%",
    direction: "rtl",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...inputValueTypography(theme),
  },
  selectPlaceholder: {
    display: "block",
    width: "100%",
    direction: "rtl",
    ...inputValueTypography(theme),
    color: theme.palette.text.disabled,
  },
  checkboxBox: {
    display: "flex",
    flexDirection: "row",
    direction: "rtl",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
    height: 48,
    width: "100%",
    padding: "12px 16px 12px 8px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    backgroundColor: theme.palette.background.paper,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    alignSelf: "stretch",
  },
  btn: {
    height: 48,
    minHeight: 48,
    maxHeight: 48,
    padding: "12px 16px 12px 8px",
    borderRadius: "4px",
    borderWidth: "1px",
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: "24px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    boxSizing: "border-box",
  },
  textarea: {
    direction: "rtl",
    margin: 0,
    "& .MuiOutlinedInput-root": {
      margin: 0,
      borderRadius: "4px",
      backgroundColor: theme.palette.background.paper,
      ...inputValueTypography(theme),
      color: theme.palette.secondary.main,
      boxSizing: "border-box",
      alignItems: "flex-start",
      "& fieldset": {
        borderColor: theme.palette.divider,
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px 12px 8px",
      boxSizing: "border-box",
      ...inputValueTypography(theme),
      color: theme.palette.secondary.main,
    },
  },
}));
